import type { ProductListModule } from 'types/capi/modules';
import type { Affiliate } from 'types/capi/shared';

import AffiliatePageLink from '~/components/AffiliatePageLink';
import Image from '~/components/Image';
import ProductCard from '~/components/ProductCard';

type Props = {
  backgroundColour?: 'bg-white' | 'bg-grey-tile';
  module: ProductListModule;
  moduleIndex?: number;
};

export default function ProductList({
  module,
  moduleIndex,
  backgroundColour = 'bg-white'
}: Props) {
  return (
    <div className={`w-full ${backgroundColour}`}>
      <div className="flex flex-col items-center justify-center gap-6 px-0 py-10 md:mx-auto md:max-w-6xl md:gap-10 md:py-10">
        <div className="flex flex-col gap-2 self-stretch py-0 pl-4 md:gap-10">
          <div className="flex flex-col gap-2">
            <div className="flex flex-col gap-2 md:flex-row md:gap-6">
              <AffiliateLogo
                affiliate={module.cta.affiliate}
                className="w-16"
              />
              <div className="flex flex-col gap-2 text-sm md:text-base">
                <h2 className="flex flex-col justify-items-center text-left text-2xl leading-10 sm:text-[32px] md:text-[40px] md:leading-[48px]">
                  {module.header}
                </h2>
                {module.description}
              </div>
            </div>
          </div>
          <Products module={module} moduleIndex={moduleIndex} />
        </div>
        <CtaButton module={module} />
      </div>
    </div>
  );
}

function Products({ module, moduleIndex = 0 }: Props) {
  return (
    <div className="custom-scrollbar flex overflow-x-scroll md:block md:overflow-x-auto">
      <div className="grid shrink-0 grid-cols-4 gap-2 md:gap-6">
        {module.items.map((product, index) => (
          <ProductCard.External
            key={`product-${product.id}-${index}`}
            product={product}
            padImage
            className="h-auto w-48 md:w-full"
            allowNotifications
            imageLoading="eager"
            cardSize="small"
            showNotificationCount={true}
            pagePosition={`Sale Product List-${moduleIndex}`}
            overrideInteractionPagePosition={true}
          />
        ))}
      </div>
    </div>
  );
}

function AffiliateLogo({
  affiliate,
  className = ''
}: {
  affiliate: Affiliate;
  className?: string;
}) {
  return (
    <Image
      src={affiliate.logo}
      className={`rounded ${className}`}
      alt={affiliate.name}
      loading="lazy"
      height={150}
      width={150}
      responsiveOptions={{ sm: { height: 150, pad: true, width: 150 } }}
      pad
    />
  );
}

function CtaButton({ module }: Props) {
  return (
    <div className="flex h-full w-[360px] flex-col gap-2 font-bold">
      <AffiliatePageLink
        affiliate={module.cta.affiliate}
        urlTemplates={module.cta}
        variant="forest"
        className="flex h-12 items-center justify-center gap-2"
      >
        <AffiliateLogo affiliate={module.cta.affiliate} className="w-7" />
        {module.cta.text}
      </AffiliatePageLink>
    </div>
  );
}
